/**
 * WISHLIST MODULE — Prestige Theme (Tabbed Drawer)
 * localStorage-based wishlist integrated into the cart drawer with tabs
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'shopify_wishlist';

  /* ========================================================================
     DATA LAYER
     ======================================================================== */

  function getWishlist() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  function saveWishlist(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }

  function addItem(productData) {
    const items = getWishlist();
    if (!items.find((i) => i.id === productData.id)) {
      items.push(productData);
      saveWishlist(items);
    }
    syncUI();
  }

  function removeItem(productId) {
    const items = getWishlist().filter((i) => i.id !== productId);
    saveWishlist(items);
    syncUI();
  }

  function isInWishlist(productId) {
    return getWishlist().some((i) => i.id === productId);
  }

  function clearWishlist() {
    saveWishlist([]);
    syncUI();
  }

  /* ========================================================================
     TAB SWITCHING
     ======================================================================== */

  function switchTab(tabName) {
    // Update tab buttons
    document.querySelectorAll('[data-drawer-tab]').forEach((tab) => {
      tab.classList.toggle('cart-wishlist-tab--active', tab.getAttribute('data-drawer-tab') === tabName);
    });

    // Update panels
    document.querySelectorAll('[data-drawer-panel]').forEach((panel) => {
      const isActive = panel.getAttribute('data-drawer-panel') === tabName;
      panel.style.display = isActive ? '' : 'none';
      panel.classList.toggle('cart-wishlist-panel--active', isActive);
    });

    // Update footers
    document.querySelectorAll('[data-drawer-footer]').forEach((footer) => {
      const isActive = footer.getAttribute('data-drawer-footer') === tabName;
      footer.style.display = isActive ? '' : 'none';
    });

    // If switching to wishlist, render items
    if (tabName === 'wishlist') {
      renderDrawerItems();
    }
  }

  /* ========================================================================
     OPEN DRAWER TO SPECIFIC TAB
     ======================================================================== */

  function openDrawerToTab(tabName) {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;

    // Switch tab first
    switchTab(tabName);

    // Remove center-body class so content shows properly
    drawer.classList.remove('drawer--center-body');

    // Open using Prestige's drawer methods
    if (typeof drawer.show === 'function') {
      drawer.show();
    } else {
      drawer.setAttribute('open', '');
    }
  }

  /* ========================================================================
     UI SYNC
     ======================================================================== */

  function syncUI() {
    syncHearts();
    syncCounts();
    renderDrawerItems();
  }

  function syncHearts() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      const id = btn.getAttribute('data-product-id');
      btn.classList.toggle('is-active', isInWishlist(id));
      btn.setAttribute(
        'aria-label',
        isInWishlist(id) ? 'Remove from wishlist' : 'Add to wishlist'
      );
    });
  }

  function syncCounts() {
    const count = getWishlist().length;
    document.querySelectorAll('wishlist-count').forEach((el) => {
      el.textContent = count;
    });

    document.querySelectorAll('.wishlist-dot').forEach((dot) => {
      dot.classList.toggle('is-visible', count > 0);
    });
  }

  /* ========================================================================
     DRAWER RENDERING
     ======================================================================== */

  function formatMoney(cents) {
    if (!cents && cents !== 0) return '';
    return (
      '$' +
      (parseInt(cents, 10) / 100).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  }

  function renderDrawerItems() {
    const container = document.querySelector('[data-wishlist-items]');
    const emptyState = document.querySelector('[data-wishlist-empty]');
    const wishlistFooter = document.querySelector('[data-drawer-footer="wishlist"]');
    if (!container) return;

    const items = getWishlist();

    if (items.length === 0) {
      container.style.display = 'none';
      container.innerHTML = '';
      if (emptyState) emptyState.style.display = '';
      if (wishlistFooter) wishlistFooter.style.display = 'none';
      return;
    }

    if (emptyState) emptyState.style.display = 'none';
    container.style.display = '';

    // Only show wishlist footer if wishlist tab is active
    const wishlistPanel = document.querySelector('[data-drawer-panel="wishlist"]');
    if (wishlistFooter && wishlistPanel && wishlistPanel.classList.contains('cart-wishlist-panel--active')) {
      wishlistFooter.style.display = '';
    }

    container.innerHTML = items
      .map((item) => {
        const available = item.available === 'true' || item.available === true;
        const hasCompare =
          item.comparePrice &&
          item.comparePrice !== '' &&
          item.comparePrice !== '0' &&
          item.comparePrice !== item.price;

        return `
        <div class="wishlist-item" data-wishlist-item-id="${item.id}">
          <a href="${item.url}">
            <img
              src="${item.image}"
              alt="${item.title}"
              class="wishlist-item__image"
              loading="lazy"
              width="80"
              height="80"
            />
          </a>
          <div class="wishlist-item__info">
            <p class="wishlist-item__vendor">${item.vendor}</p>
            <a href="${item.url}" class="wishlist-item__title">${item.title}</a>
            <span class="wishlist-item__status ${available ? 'wishlist-item__status--in-stock' : 'wishlist-item__status--out-of-stock'}">
              ${available ? 'In Stock' : 'Out of Stock'}
            </span>
            <div class="wishlist-item__prices">
              <p class="wishlist-item__price">${formatMoney(item.price)}</p>
              ${hasCompare ? `<p class="wishlist-item__compare-price">${formatMoney(item.comparePrice)}</p>` : ''}
            </div>
            <div class="wishlist-item__actions">
              <button
                type="button"
                class="wishlist-item__add-to-cart"
                data-wishlist-add-to-cart="${item.variantId}"
                ${!available ? 'disabled' : ''}
              >
                ${available ? 'Add to Cart' : 'Unavailable'}
              </button>
              <button
                type="button"
                class="wishlist-item__delete"
                data-wishlist-remove="${item.id}"
                aria-label="Remove from wishlist"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  <line x1="10" y1="11" x2="10" y2="17"/>
                  <line x1="14" y1="11" x2="14" y2="17"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      `;
      })
      .join('');
  }

  /* ========================================================================
     ADD TO CART
     ======================================================================== */

  async function addToCart(variantId) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] }),
      });

      if (!res.ok) throw new Error('Failed to add to cart');

      // Re-fetch cart drawer section to update cart content
      try {
        const sectionRes = await fetch('/?sections=cart-drawer');
        if (sectionRes.ok) {
          const sections = await sectionRes.json();
          const cartDrawerEl = document.getElementById('cart-drawer');

          if (cartDrawerEl && sections['cart-drawer']) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');

            // Only update the cart panel content, not the entire drawer
            const newCartPanel = doc.querySelector('[data-drawer-panel="cart"]');
            const currentCartPanel = document.querySelector('[data-drawer-panel="cart"]');
            if (newCartPanel && currentCartPanel) {
              currentCartPanel.innerHTML = newCartPanel.innerHTML;
            }

            // Update cart count
            const cartRes = await fetch('/cart.js');
            const cartData = await cartRes.json();
            document.querySelectorAll('cart-count').forEach((el) => {
              el.textContent = cartData.item_count;
            });

            // Update cart dot
            document.querySelectorAll('.header__cart-dot').forEach((dot) => {
              dot.classList.toggle('is-visible', cartData.item_count > 0);
            });

            // Update cart footer
            const newCartFooter = doc.querySelector('[data-drawer-footer="cart"]');
            const currentCartFooter = document.querySelector('[data-drawer-footer="cart"]');
            if (newCartFooter && currentCartFooter) {
              currentCartFooter.innerHTML = newCartFooter.innerHTML;
              currentCartFooter.style.display = '';
            }
          }
        }

        // Switch to cart tab to show the item was added
        switchTab('cart');

      } catch (e) {
        window.location.reload();
      }
    } catch (err) {
      console.error('Wishlist: Add to cart failed', err);
      alert('Could not add item to cart. Please try again.');
    }
  }

  /* ========================================================================
     EVENT LISTENERS
     ======================================================================== */

  function initEvents() {
    document.addEventListener('click', (e) => {
      // Tab switching
      const tabBtn = e.target.closest('[data-drawer-tab]');
      if (tabBtn) {
        e.preventDefault();
        switchTab(tabBtn.getAttribute('data-drawer-tab'));
        return;
      }

      // Heart toggle
      const heartBtn = e.target.closest('[data-wishlist-toggle]');
      if (heartBtn) {
        e.preventDefault();
        e.stopPropagation();

        const id = heartBtn.getAttribute('data-product-id');

        if (isInWishlist(id)) {
          removeItem(id);
        } else {
          addItem({
            id: id,
            handle: heartBtn.getAttribute('data-product-handle'),
            title: heartBtn.getAttribute('data-product-title'),
            vendor: heartBtn.getAttribute('data-product-vendor'),
            url: heartBtn.getAttribute('data-product-url'),
            price: heartBtn.getAttribute('data-product-price'),
            comparePrice: heartBtn.getAttribute('data-product-compare-price'),
            image: heartBtn.getAttribute('data-product-image'),
            available: heartBtn.getAttribute('data-product-available'),
            variantId: heartBtn.getAttribute('data-variant-id'),
          });
        }
        return;
      }

      // Open drawer to wishlist tab (header icon)
      const openBtn = e.target.closest('[data-wishlist-open]');
      if (openBtn) {
        e.preventDefault();
        openDrawerToTab('wishlist');
        return;
      }

      // Remove from drawer
      const removeBtn = e.target.closest('[data-wishlist-remove]');
      if (removeBtn) {
        e.preventDefault();
        removeItem(removeBtn.getAttribute('data-wishlist-remove'));
        return;
      }

      // Add to cart from drawer
      const cartBtn = e.target.closest('[data-wishlist-add-to-cart]');
      if (cartBtn && !cartBtn.disabled) {
        e.preventDefault();
        const variantId = cartBtn.getAttribute('data-wishlist-add-to-cart');
        cartBtn.disabled = true;
        cartBtn.textContent = 'Adding...';
        addToCart(variantId).finally(() => {
          cartBtn.disabled = false;
          cartBtn.textContent = 'Add to Cart';
        });
        return;
      }

      // Clear wishlist
      const clearBtn = e.target.closest('[data-wishlist-clear]');
      if (clearBtn) {
        e.preventDefault();
        clearWishlist();
        return;
      }
    });

    // When cart drawer opens via cart icon, ensure cart tab is active
    const cartDrawer = document.getElementById('cart-drawer');
    if (cartDrawer) {
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
            if (cartDrawer.hasAttribute('open')) {
              // Drawer just opened — check if it was triggered by cart icon
              // If no tab was explicitly set to wishlist, default to cart
              const activeTab = document.querySelector('.cart-wishlist-tab--active');
              if (!activeTab) {
                switchTab('cart');
              }
            }
          }
        }
      });
      observer.observe(cartDrawer, { attributes: true });
    }

    // Intercept cart icon clicks to switch to cart tab
    document.querySelectorAll('.header__cart-link a').forEach((cartLink) => {
      cartLink.addEventListener('click', () => {
        // When cart icon is clicked, ensure cart tab is active when drawer opens
        switchTab('cart');
      });
    });
  }

  /* ========================================================================
     INIT
     ======================================================================== */

  function init() {
    initEvents();
    syncUI();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
